import { Box, Grid } from "@mui/material"
import Dropdown from "components/Dropdown"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import SearchComponent from "components/SearchComponent"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { useDebounce } from "hooks/utils"
import React, { Fragment, useEffect, useState } from "react"
import { Col, Container, Row } from "react-bootstrap"
import { useHistory } from "react-router-dom"
import ExpandableTable from "components/ExpandableTable"
import { useDispatch, useSelector } from "react-redux"
import {
  exportLeaderboardDataAction,
  exportNewLeaderboardDataAction,
  getGroupLeaderBoardAction,
  getNewGroupLeaderBoardAction
} from "redux/DMV/actions/user"
import { appActions } from "redux/app/slice/app"

const GroupLeaderboard = () => {
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [paginatedLeaderboardList, setPaginatedLeaderboardList] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pages, setPages] = useState(1)
  const { groupLeaderboard, groupId } = useSelector((state) => state?.dmv)
  const { date, employeeClass, orderBy, startDate, endDate, searchField } = useSelector(
    (state) => state?.app?.filters || ""
  )
  const debouncedSearch = useDebounce(searchField, 500)
  const { userRestaurant } = useSelector((state) => state?.user)
  const dispatch = useDispatch()
  const history = useHistory()

  const handleOtherLeaderboardRoute = (e) => {
    const path =
      e === "leaderboard"
        ? "/marketing/dashboard/leaderboard"
        : `/marketing/dashboard/leaderboard/${e}-leaderboard`
    history.push(path)
  }

  const handleExport = () => {
    const filters = { groupId, date, startDate, endDate }
    if (userRestaurant?.new_user_journey) {
      dispatch(exportNewLeaderboardDataAction(filters))
    } else {
      dispatch(exportLeaderboardDataAction(filters))
    }
  }
  useEffect(() => {
    const itemsPerPage = 10
    const source = Array.isArray(groupLeaderboard) ? groupLeaderboard : []
    const filtered = debouncedSearch?.trim()
      ? source.filter((item) =>
          [item?.name, item?.restaurant].some((field) =>
            field?.toLowerCase().includes(debouncedSearch.trim().toLowerCase())
          )
        )
      : source
    const totalPages = Math.ceil(filtered.length / itemsPerPage)
    const paginatedLeaderboardList = filtered.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    )
    setPages(totalPages)
    setPaginatedLeaderboardList(paginatedLeaderboardList)
  }, [groupLeaderboard, date, groupId, currentPage, employeeClass, debouncedSearch])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    const role = employeeClass ? employeeClass : "All"
    const sort = orderBy ? orderBy : "total_coins"
    const filters = {
      groupId,
      date,
      employeeClass: role,
      sort_by: sort,
      start_date: startDate,
      end_date: endDate
    }
    setCurrentPage(1)
    if (userRestaurant?.new_user_journey) {
      dispatch(getNewGroupLeaderBoardAction(filters))
    } else {
      dispatch(getGroupLeaderBoardAction(filters))
    }
  }, [groupId, date, employeeClass, orderBy, startDate, endDate])

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs())
    setCurrentPage(1)
    document.title = "Group Leaderboard"
    dispatch(appActions.initFilters())
  }, [])

  return (
    <Fragment>
      <Container fluid className="p-0">
        <Row>
          <Col lg="12">
            <Header title="Group Leaderboard" breadCrumbs={breadCrumbs}>
              <Grid container item gap={2} alignItems="center">
                <SearchComponent />
                <AppButton sx={{ padding: "13px 36px" }} onClick={handleExport}>
                  Export to Excel
                </AppButton>
                <Dropdown
                  menuItems={LeaderboardOptions}
                  onChange={(e) => {
                    handleOtherLeaderboardRoute(e)
                  }}>
                  <Box role="button">
                    <AppButton sx={{ padding: "13px 36px" }}>Other Leaderboard</AppButton>
                  </Box>
                </Dropdown>
              </Grid>
            </Header>
          </Col>
        </Row>
        <Row>
          <Col lg="12">
            <ExpandableTable
              setPage={setCurrentPage}
              currentPage={currentPage}
              totalPage={pages}
              rowsData={paginatedLeaderboardList}
              columns={userRestaurant?.new_user_journey ? newColumns : columns}
              expandMapping={
                userRestaurant?.new_user_journey ? newExpandedColumns : expandedColumns
              }
              isGroupLeaderboard={true}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  )
}

export default GroupLeaderboard

const LeaderboardOptions = [
  { label: "Frontline Performance", value: "leaderboard" },
  { label: "Quiz Leaderboard", value: "quiz" },
  { label: "Gamification Leaderboard", value: "gamification" }
  // { label: "National Leaderboard", value: "national" }
]

const columns = [
  {
    id: "rank",
    label: "Rank",
    minWidth: 50
  },
  {
    id: "name",
    label: "Name",
    minWidth: 280
  },
  {
    id: "restaurant",
    label: "Restaurant",
    align: "center",
    minWidth: 170
  },
  {
    id: "role",
    label: "Role",
    align: "center",
    minWidth: 160
  },
  {
    id: "streak",
    label: "Streak Count",
    align: "center",
    minWidth: 165,
    isSorting: true,
    sortId: "streak"
  },
  {
    id: "total_coins",
    label: "Total Coins Earned",
    align: "center",
    minWidth: 220,
    isSorting: true,
    sortId: "total_coins",
    expandAction: true
  },
  {
    id: "games_played",
    label: "Total Games Played",
    align: "center",
    minWidth: 250,
    // isSorting: true,
    sortId: "games_played",
    expandAction: true
  },
  {
    id: "trivia_coins",
    label: "Trivia Coins",
    align: "center",
    minWidth: 155,
    isSorting: true,
    sortId: "trivia_coins",
    isHidden: true
  },
  {
    id: "quiz_coins",
    label: "Quiz Coins",
    align: "center",
    minWidth: 155,
    isSorting: true,
    sortId: "quiz_coins",
    isHidden: true
  },
  {
    id: "bonus_coins",
    label: "Bonus Coins",
    align: "center",
    minWidth: 155,
    isSorting: true,
    sortId: "bonus_coins",
    isHidden: true
  },
  {
    id: "trivia_played",
    label: "Trivia Played",
    align: "center",
    minWidth: 155,
    sort_Id: "trivia_played",
    isHidden: true
  },
  {
    id: "quiz_played",
    label: "Quiz Played",
    minWidth: 155,
    align: "center",
    sort_Id: "quiz_played",
    isHidden: true
  }
]
const expandedColumns = {
  total_coins: ["trivia_coins", "quiz_coins", "bonus_coins"],
  games_played: ["trivia_played", "quiz_played"]
}
const newColumns = [
  {
    id: "rank",
    label: "Rank",
    minWidth: 50
  },
  {
    id: "name",
    label: "Name",
    minWidth: 280
  },
  {
    id: "restaurant",
    label: "Restaurant",
    align: "center",
    minWidth: 170
  },
  {
    id: "role",
    label: "Role",
    align: "center",
    minWidth: 160
  },
  {
    id: "streak",
    label: "Streak Count",
    align: "center",
    minWidth: 165,
    isSorting: true,
    sortId: "streak"
  },
  {
    id: "history_weekly_challenges",
    label: "History Challenges Played",
    align: "center",
    minWidth: 200,
    isHidden: true
  },
  {
    id: "total_coins",
    label: "Total Coins Earned",
    align: "center",
    minWidth: 220,
    isSorting: true,
    sortId: "total_coins",
    expandAction: true
  },
  {
    id: "games_played",
    label: "Total Games Played",
    align: "center",
    minWidth: 250,
    // isSorting: true,
    sortId: "games_played",
    expandAction: true
  },
  {
    id: "weekly_coins",
    label: "Weekly Coins",
    align: "center",
    minWidth: 155,
    isSorting: true,
    sortId: "weekly_coins",
    isHidden: true
  },
  {
    id: "quiz_coins",
    label: "Quiz Coins",
    align: "center",
    minWidth: 155,
    isSorting: true,
    sortId: "quiz_coins",
    isHidden: true
  },
  {
    id: "bonus_coins",
    label: "Bonus Coins",
    align: "center",
    minWidth: 155,
    isSorting: true,
    sortId: "bonus_coins",
    isHidden: true
  },
  {
    id: "weekly_challenges_played",
    label: "Weekly Challenges Played",
    minWidth: 155,
    align: "center",
    sort_Id: "weekly_challenges_played",
    isHidden: true
  },
  {
    id: "quiz_played",
    label: "Quiz Games Played",
    minWidth: 155,
    align: "center",
    sort_Id: "quiz_played",
    isHidden: true
  },
  {
    id: "daily_practice_attempts",
    label: "Random Questions Played",
    minWidth: 155,
    align: "center",
    sort_Id: "daily_practice_attempts",
    isHidden: true
  },
  {
    id: "wine_region_attempts",
    label: "Wine Region Attempts",
    minWidth: 155,
    align: "center",
    sort_Id: "wine_region_attempts",
    isHidden: true
  },
  {
    id: "mistake_attempts",
    label: "Mistake Attempts",
    minWidth: 155,
    align: "center",
    sort_Id: "mistake_attempts",
    isHidden: true
  }
]
const newExpandedColumns = {
  total_coins: ["weekly_coins", "quiz_coins", "bonus_coins"],
  games_played: [
    "weekly_challenges_played",
    "quiz_played",
    "mistake_attempts",
    "wine_region_attempts",
    "daily_practice_attempts",
    "history_weekly_challenges"
  ]
}
